import React, { useMemo } from 'react'
import { clientUniqueId } from '@utils/secureRandom'
import { Spinner } from './Spinner'

/**
 * Standardized loading state component
 *
 * Provides consistent loading UI across the application with different sizes and display options.
 */

export interface LoadingStateProps {
  /** Loading message to display */
  message?: string
  /** Size variant */
  size?: 'sm' | 'md' | 'lg'
  /** Whether to show as an overlay */
  overlay?: boolean
  /** Whether to show the spinner */
  showSpinner?: boolean
  /** Custom CSS class */
  className?: string
  /** Custom styles */
  style?: React.CSSProperties
}

const sizeClasses = {
  sm: 'loading-state--sm',
  md: 'loading-state--md',
  lg: 'loading-state--lg',
}

const sizeSpinnerSizes: Record<'sm' | 'md' | 'lg', 'xs' | 'sm' | 'md' | 'lg'> = {
  sm: 'sm',
  md: 'md',
  lg: 'lg',
}

/**
 * LoadingState component for consistent loading UI
 *
 * @example
 * ```tsx
 * // Basic usage
 * <LoadingState message="Loading data..." />
 *
 * // Large overlay
 * <LoadingState size="lg" overlay message="Processing..." />
 *
 * // Small inline
 * <LoadingState size="sm" showSpinner={false} message="Saving..." />
 * ```
 */
export const LoadingState = React.memo<LoadingStateProps>(
  ({
    message = 'Loading...',
    size = 'md',
    overlay = false,
    showSpinner = true,
    className = '',
    style,
  }) => {
    const containerClasses = [
      'loading-state',
      sizeClasses[size],
      overlay && 'loading-state--overlay',
      className,
    ]
      .filter(Boolean)
      .join(' ')

    const content = (
      <div className="loading-state__content">
        {showSpinner && <Spinner size={sizeSpinnerSizes[size]} />}
        {message && <span className="loading-state__message">{message}</span>}
      </div>
    )

    if (overlay) {
      return (
        <div className={containerClasses} style={style}>
          <div className="loading-state__overlay">{content}</div>
        </div>
      )
    }

    return (
      <div className={containerClasses} style={style}>
        {content}
      </div>
    )
  }
)

LoadingState.displayName = 'LoadingState'

/**
 * Page-level loading component
 * Use this for full page loading states
 */
export const PageLoader: React.FC<{
  message?: string
  className?: string
}> = ({ message = 'Loading page...', className = '' }) => (
  <div className={`page-loader ${className}`}>
    <LoadingState size="lg" message={message} />
  </div>
)

/**
 * Inline loading component for buttons and small areas
 */
export const InlineLoader: React.FC<{
  message?: string
  size?: 'sm' | 'md'
}> = ({ message, size = 'sm' }) => (
  <LoadingState size={size} message={message} overlay={false} className="inline-loader" />
)

/**
 * Skeleton loading component for content placeholders
 */
export const SkeletonLoader: React.FC<{
  lines?: number
  className?: string
}> = ({ lines = 3, className = '' }) => {
  const lineIds = useMemo(
    () => Array.from({ length: lines }, () => clientUniqueId('skeleton-line')),
    [lines]
  )
  return (
    <div className={`skeleton-loader ${className}`}>
      {lineIds.map((id) => (
        <div key={id} className="skeleton-loader__line" />
      ))}
    </div>
  )
}
